import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  CardNumberElement,
  CardCVCElement,
  CardExpiryElement,
  injectStripe,
} from 'react-stripe-elements';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import segmentEvents from 'EventTracking/consts/segmentEvents';
import { dataAttrs } from 'Helpers/Testing';
import {
  FDHeadlineText, FDStack,
} from '@facet-wealth/design';
import { useLeadPropsContext } from 'Context/LeadPropsContext';

const StripeInput = ({ label, condensed }) => {
  const theme = useTheme();
  const stripeElementStyle = useMemo(() => ({
    base: {
      '::placeholder': {
        color: theme.palette.facet.oceana800 ?? theme.palette.facet.primaryBlue,
        fontWeight: theme.typography.fontWeightBold,
      },
      fontSize: '16px', // We can't use rem here because it's in an iframe, 1rem means something totally different
      fontWeight: theme.typography.fontWeightBold,
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.facet.oceana800 ?? theme.palette.facet.primaryBlue,
    },
  }), [theme]);
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({ nodeName: 'payment-stripe-elements' });
  const leadProps = useLeadPropsContext();
  const logCVCEntered = useCallback(({ complete }) => {
    if (complete) logEvent(segmentEvents.leadEnteredCVC, leadProps);
  }, [logEvent]);

  const expiryAndCVC = (
    <>
      <CardExpiryElement
        className={'StripeCardExpiryInput'}
        placeholder={'Expiration Date'}
        style={stripeElementStyle}
      />
      <CardCVCElement
        placeholder={'Security Code'}
        style={stripeElementStyle}
        onChange={logCVCEntered}
      />
    </>
  );

  return (
    <div {...rootNodeProps} {...dataAttrs('credit card inputs')}>
      <FDStack spacing={2} direction="column">
        <FDHeadlineText size="sm" value={label} />
        <CardNumberElement
          className={'StripeCardNumberInput'}
          placeholder={'Credit Card Number'}
          style={stripeElementStyle}
        />
        {condensed ? <FDStack direction="row" spacing={1}>{expiryAndCVC}</FDStack> : expiryAndCVC}
      </FDStack>
    </div>
  );
};

StripeInput.defaultProps = {
};

StripeInput.propTypes = {
  stripe: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  condensed: PropTypes.bool,
};

export default injectStripe(StripeInput);
